/**
 * auctionSession.js — Gate Nuovo/Carica, load/save asta attiva, Impostazioni
 */

import { SessionPersistence } from '../services/SessionPersistence.js';
import {
  DEFAULT_AUCTION_NAME,
  DEFAULT_EXIT_MODE,
  DEFAULT_ROSA_BUDGET,
  DEFAULT_ROSA_MAX_PLAYERS,
  DEFAULT_ROSA_ROLE_TARGETS,
  EXIT_MODE_ASSIGNED,
  EXIT_MODE_SIMPLE,
  createAuction,
  deleteAuction,
  fetchAuction,
  updateAuction,
} from '../services/db/auctions.pocketbase.js';
import {
  fetchAuctionExits,
  normalizeOpponentId,
  upsertAuctionExit,
  deleteAuctionExit,
} from '../services/db/auctionExits.pocketbase.js';
import { fetchRoster, deleteRosterPlayer } from '../services/db/roster.pocketbase.js';
import {
  opponentPlaceholder,
  readOpponentNamesFromForm,
  syncOpponentSlots,
} from './auctionOpponents.js';
import { escapeHtml } from '../utils/escapeHtml.js';

function applyAuctionToState(app, auction) {
  app.state.activeAuctionId = auction?.id || null;
  app.state.activeAuctionName = auction?.name || '';
  app.state.rosaBudget = auction?.budget ?? DEFAULT_ROSA_BUDGET;
  app.state.rosaMaxPlayers = auction?.max_players ?? DEFAULT_ROSA_MAX_PLAYERS;
  app.state.rosaNumParticipants = auction?.num_participants ?? null;
  app.state.rosaRoleTargets = auction?.role_targets
    ? { ...auction.role_targets }
    : { ...DEFAULT_ROSA_ROLE_TARGETS };
  app.state.rosaExitMode = auction?.exit_mode ?? DEFAULT_EXIT_MODE;
  app.state.auctionOpponents = Array.isArray(auction?.opponents)
    ? auction.opponents.map((o) => ({ id: o.id, name: o.name }))
    : [];
}

export const auctionSessionMethods = {
  async ensureActiveAuction() {
    if (this._ensureActiveAuctionPromise) {
      return this._ensureActiveAuctionPromise;
    }
    this._ensureActiveAuctionPromise = this._ensureActiveAuctionImpl();
    try {
      return await this._ensureActiveAuctionPromise;
    } finally {
      this._ensureActiveAuctionPromise = null;
    }
  },

  async _ensureActiveAuctionImpl() {
    if (!this.state.userSession) return false;
    const accessKey = this.state.userSession.getAccessCode();
    if (!accessKey) return false;

    const storedId = SessionPersistence.getActiveAuctionId();
    if (storedId) {
      try {
        const auction = await fetchAuction(accessKey, storedId);
        if (auction) {
          await this.activateAuction(auction);
          return true;
        }
      } catch (err) {
        console.error('[Asta] Validazione asta attiva fallita:', err);
      }
      SessionPersistence.setActiveAuctionId(null);
    }

    this.showUnifiedSetupScreen();
    return false;
  },

  async activateAuction(auction) {
    applyAuctionToState(this, auction);
    SessionPersistence.setActiveAuctionId(auction.id);
    await this.loadAuctionData();
    if (typeof this.refreshUnifiedSetupAfterAuctionChange === 'function') {
      await this.refreshUnifiedSetupAfterAuctionChange();
    }
    this.closeAuctionSettings?.();
    if (typeof this.syncAuctionModeListFilters === 'function') {
      this.syncAuctionModeListFilters();
    }
    if (typeof this.updateRosaTabCount === 'function') {
      this.updateRosaTabCount();
    }
    if (typeof this.syncStudioButtonsInTable === 'function') {
      this.syncStudioButtonsInTable();
    }
  },

  async loadAuctionData() {
    if (!this.state.userSession || !this.state.activeAuctionId) {
      this.state.roster = new Map();
      this.state.auctionExits = new Map();
      this.renderRecentTakesStrip?.();
      this.renderListaKpiStrip?.();
      return;
    }
    const accessKey = this.state.userSession.getAccessCode();
    const auctionId = this.state.activeAuctionId;
    const [rosterResult, exitsResult] = await Promise.allSettled([
      fetchRoster(accessKey, auctionId),
      fetchAuctionExits(accessKey, auctionId),
    ]);

    if (rosterResult.status === 'fulfilled') {
      this.state.roster = new Map(
        rosterResult.value.map((r) => [
          r.player_key,
          {
            price: r.price,
            at: Date.parse(r.created || '') || 0,
          },
        ]),
      );
    } else {
      console.error('[Asta] Errore caricamento roster:', rosterResult.reason);
      this.state.roster = new Map();
    }

    if (exitsResult.status === 'fulfilled') {
      this.state.auctionExits = new Map(
        exitsResult.value
          .filter((r) => r.player_key)
          .map((r) => [
            r.player_key,
            {
              price: Number(r.price) || 0,
              at: Date.parse(r.created || '') || 0,
              opponentId: normalizeOpponentId(r.opponent_id),
            },
          ]),
      );
    } else {
      console.error('[Asta] Errore caricamento exits:', exitsResult.reason);
      this.state.auctionExits = new Map();
    }
    this.renderRecentTakesStrip?.();
    this.renderListaKpiStrip?.();
  },

  async createAndActivateAuction({ name, budget, maxPlayers, roleTargets } = {}) {
    const accessKey = this.state.userSession?.getAccessCode();
    if (!accessKey) throw new Error('Sessione non disponibile');
    const auction = await createAuction({
      accessKey,
      name: name || DEFAULT_AUCTION_NAME,
      budget: budget ?? DEFAULT_ROSA_BUDGET,
      maxPlayers: maxPlayers ?? DEFAULT_ROSA_MAX_PLAYERS,
      roleTargets: roleTargets || DEFAULT_ROSA_ROLE_TARGETS,
    });
    await this.activateAuction(auction);
    return auction;
  },

  async loadAndActivateAuction(auctionId) {
    const accessKey = this.state.userSession?.getAccessCode();
    if (!accessKey) throw new Error('Sessione non disponibile');
    const auction = await fetchAuction(accessKey, auctionId);
    if (!auction) throw new Error('Asta non trovata');
    await this.activateAuction(auction);
    return auction;
  },

  async saveActiveAuctionSettings(partial = {}) {
    const accessKey = this.state.userSession?.getAccessCode();
    const auctionId = this.state.activeAuctionId;
    if (!accessKey || !auctionId) throw new Error('Nessuna asta attiva');

    const name = partial.name ?? this.state.activeAuctionName;
    const budget = partial.budget ?? this.state.rosaBudget;
    const maxPlayers = partial.maxPlayers ?? this.state.rosaMaxPlayers;
    const roleTargets = partial.roleTargets ?? this.state.rosaRoleTargets;
    const numParticipants = partial.numParticipants !== undefined
      ? partial.numParticipants
      : this.state.rosaNumParticipants;
    const exitMode = partial.exitMode !== undefined
      ? partial.exitMode
      : this.state.rosaExitMode;
    const opponents = partial.opponents !== undefined
      ? partial.opponents
      : this.state.auctionOpponents;

    const auction = await updateAuction({
      accessKey,
      auctionId,
      name,
      budget,
      maxPlayers,
      roleTargets,
      numParticipants,
      exitMode,
      opponents,
    });
    applyAuctionToState(this, auction);
    this.state.rosaSettingsError = null;
    return auction;
  },

  async deleteActiveAuction() {
    const accessKey = this.state.userSession?.getAccessCode();
    const auctionId = this.state.activeAuctionId;
    if (!accessKey || !auctionId) return;
    await deleteAuction(accessKey, auctionId);
    SessionPersistence.setActiveAuctionId(null);
    applyAuctionToState(this, null);
    this.state.roster = new Map();
    this.state.auctionExits = new Map();
    this.showUnifiedSetupScreen();
  },

  /**
   * Segna uscito. `opponentId` opzionale:
   * - undefined → non tocca l’eventuale assegnazione già salvata
   * - null / '' → Da assegnare
   * - stringa → id avversario
   * Se era in Rosa, lo toglie prima (cambio assegnazione).
   */
  async markAuctionExited(playerKey, price, opponentId) {
    if (!playerKey || !this.state.activeAuctionId) return;

    const accessKey = this.state.userSession?.getAccessCode();
    if (!accessKey) return;
    const n = Number(price);
    if (!Number.isFinite(n) || n < 1) {
      throw new Error('Il prezzo deve essere ≥ 1');
    }
    try {
      if (this.state.roster instanceof Map && this.state.roster.has(playerKey)) {
        await deleteRosterPlayer(accessKey, this.state.activeAuctionId, playerKey);
        this.state.roster.delete(playerKey);
        this.updateRosaTabCount?.();
      }
      const upsertArgs = {
        accessKey,
        auctionId: this.state.activeAuctionId,
        playerKey,
        price: n,
      };
      if (opponentId !== undefined) {
        upsertArgs.opponentId = opponentId;
      }
      await upsertAuctionExit(upsertArgs);
      if (!(this.state.auctionExits instanceof Map)) {
        this.state.auctionExits = new Map();
      }
      const prev = this.state.auctionExits.get(playerKey);
      const prevAt = Number(prev?.at) || 0;
      const entry = { price: n, at: prevAt || Date.now() };
      if (opponentId !== undefined) {
        entry.opponentId = normalizeOpponentId(opponentId);
      } else if (prev && Object.prototype.hasOwnProperty.call(prev, 'opponentId')) {
        entry.opponentId = prev.opponentId ?? null;
      } else {
        entry.opponentId = null;
      }
      this.state.auctionExits.set(playerKey, entry);
      if (typeof this.syncAuctionModeListFilters === 'function') {
        this.syncAuctionModeListFilters();
      }
      if (typeof this.syncStudioButtonsInTable === 'function') {
        this.syncStudioButtonsInTable();
      }
      this.renderRecentTakesStrip?.();
      this.renderListaKpiStrip?.();
      const rosaScreen = document.getElementById('screen-rosa');
      if (rosaScreen && !rosaScreen.hidden) {
        this.renderRosaView?.();
      }
      const studioScreen = document.getElementById('screen-studio');
      if (studioScreen && !studioScreen.hidden) {
        this.renderStudioView?.();
      }
      const analyticsScreen = document.getElementById('screen-analytics');
      if (analyticsScreen && !analyticsScreen.hidden) {
        this.renderAnalyticsView?.();
      }
      const roseAltriScreen = document.getElementById('screen-rose-altri');
      if (roseAltriScreen && !roseAltriScreen.hidden) {
        this.renderRoseAltriView?.();
      }
    } catch (err) {
      console.error('[Asta] Errore mark exit:', err);
      throw err;
    }
  },

  async unmarkAuctionExited(playerKey) {
    if (!playerKey || !this.state.activeAuctionId) return;
    const accessKey = this.state.userSession?.getAccessCode();
    if (!accessKey) return;
    try {
      await deleteAuctionExit(accessKey, this.state.activeAuctionId, playerKey);
      this.state.auctionExits.delete(playerKey);
      if (typeof this.syncAuctionModeListFilters === 'function') {
        this.syncAuctionModeListFilters();
      }
      if (typeof this.syncStudioButtonsInTable === 'function') {
        this.syncStudioButtonsInTable();
      }
      this.renderRecentTakesStrip?.();
      this.renderListaKpiStrip?.();
      const studioScreen = document.getElementById('screen-studio');
      if (studioScreen && !studioScreen.hidden) {
        this.renderStudioView?.();
      }
      const analyticsScreen = document.getElementById('screen-analytics');
      if (analyticsScreen && !analyticsScreen.hidden) {
        this.renderAnalyticsView?.();
      }
      const roseAltriScreen = document.getElementById('screen-rose-altri');
      if (roseAltriScreen && !roseAltriScreen.hidden) {
        this.renderRoseAltriView?.();
      }
    } catch (err) {
      console.error('[Asta] Errore unmark exit:', err);
    }
  },

  isPlayerGoneInActiveAuction(playerKey) {
    if (!playerKey) return false;
    if (this.state.roster instanceof Map && this.state.roster.has(playerKey)) return true;
    if (this.state.auctionExits instanceof Map && this.state.auctionExits.has(playerKey)) return true;
    return false;
  },

  openAuctionSettings() {
    const panel = document.getElementById('auction-settings');
    if (!panel) return;
    this._auctionSettingsDraft = null;
    this._ensureAuctionSettingsDraft(true);
    panel.hidden = false;
    this._renderAuctionSettings();
  },

  closeAuctionSettings() {
    const panel = document.getElementById('auction-settings');
    if (panel) panel.hidden = true;
    this._auctionSettingsDraft = null;
  },

  _ensureAuctionSettingsDraft(seedFromState = false) {
    if (!this._auctionSettingsDraft || seedFromState) {
      const exitMode = this.state.rosaExitMode === EXIT_MODE_ASSIGNED
        ? EXIT_MODE_ASSIGNED
        : EXIT_MODE_SIMPLE;
      const numParticipants = this.state.rosaNumParticipants;
      const opponents = syncOpponentSlots(
        this.state.auctionOpponents || [],
        numParticipants,
      );
      this._auctionSettingsDraft = {
        name: this.state.activeAuctionName || '',
        budget: Number(this.state.rosaBudget) || 0,
        maxPlayers: Number(this.state.rosaMaxPlayers) || 0,
        numParticipants,
        exitMode,
        // Full list kept when mode=simple (non cancellare spegnendo)
        opponentsStored: Array.isArray(this.state.auctionOpponents)
          ? this.state.auctionOpponents.map((o) => ({ id: o.id, name: o.name ?? '' }))
          : [],
        opponents,
      };
    }
    return this._auctionSettingsDraft;
  },

  _captureAuctionSettingsDraftFromForm(form) {
    const draft = this._ensureAuctionSettingsDraft();
    if (!form) return draft;
    draft.name = form.name?.value ?? draft.name;
    draft.budget = Number(form.budget?.value) || 0;
    draft.maxPlayers = Number(form.maxPlayers?.value) || 0;
    const npInput = form.querySelector('[name="numParticipants"]');
    draft.numParticipants = !npInput || npInput.value === ''
      ? null
      : Number(npInput.value) || null;
    const modeBtn = form.querySelector('.auction-exit-mode-seg button.is-on');
    if (modeBtn?.dataset?.exitMode) {
      draft.exitMode = modeBtn.dataset.exitMode === EXIT_MODE_ASSIGNED
        ? EXIT_MODE_ASSIGNED
        : EXIT_MODE_SIMPLE;
    }
    if (draft.exitMode === EXIT_MODE_ASSIGNED) {
      draft.opponents = readOpponentNamesFromForm(form, draft.opponents);
      draft.opponentsStored = draft.opponents.map((o) => ({ ...o }));
    }
    return draft;
  },

  _renderAuctionSettings() {
    const body = document.getElementById('auction-settings-body');
    if (!body) return;

    const draft = this._ensureAuctionSettingsDraft(!this._auctionSettingsDraft);
    const assigned = draft.exitMode === EXIT_MODE_ASSIGNED;
    const opponents = assigned
      ? syncOpponentSlots(draft.opponents, draft.numParticipants)
      : [];
    if (assigned) draft.opponents = opponents;

    const oppRows = opponents.map((o, i) => `
      <div class="auction-opp-row">
        <span>${i + 1}</span>
        <input type="text" maxlength="60" class="auction-cfg-input"
          data-opp-id="${escapeHtml(o.id)}"
          placeholder="${escapeHtml(opponentPlaceholder(i))}"
          value="${escapeHtml(o.name || '')}">
      </div>
    `).join('');

    body.innerHTML = `
      <form class="auction-cfg-form" id="auction-settings-form">
        <label class="auction-cfg-field">
          <span>Nome asta</span>
          <input name="name" type="text" required maxlength="80"
            value="${escapeHtml(draft.name || '')}" class="auction-cfg-input">
        </label>
        <div class="auction-cfg-row auction-cfg-row--metrics">
        <label class="auction-cfg-field">
          <span>Budget</span>
          <input name="budget" type="number" min="0" inputmode="numeric"
            value="${Number(draft.budget) || 0}" class="auction-cfg-input num">
        </label>
        <label class="auction-cfg-field">
          <span>Max giocatori</span>
          <input name="maxPlayers" type="number" min="0" inputmode="numeric"
            value="${Number(draft.maxPlayers) || 0}" class="auction-cfg-input num">
        </label>
        <label class="auction-cfg-field">
          <span>Partecipanti</span>
          <input name="numParticipants" type="number" min="1" inputmode="numeric"
            value="${draft.numParticipants != null ? Number(draft.numParticipants) : ''}"
            placeholder="8" class="auction-cfg-input num">
        </label>
        </div>
        <div class="auction-cfg-field">
          <span>Usciti: come li segni</span>
          <div class="auction-exit-mode-seg" role="group" aria-label="Modalità uscite">
            <button type="button" data-exit-mode="simple"
              class="${!assigned ? 'is-on' : ''}">Semplice</button>
            <button type="button" data-exit-mode="assigned"
              class="${assigned ? 'is-on' : ''}">Assegna alle rose</button>
          </div>
          <p class="auction-exit-mode-hint">${assigned
    ? 'Ogni «preso da altri» chiede quale avversario. Posti = partecipanti − 1.'
    : 'Come oggi: uscito senza dire chi. Puoi accendere l’assegnazione anche a metà asta.'}</p>
        </div>
        ${assigned ? `
        <div class="auction-cfg-field" id="auction-opponents-field">
          <span>Nomi avversari</span>
          <div class="auction-opp-list">${oppRows || '<p class="auction-exit-mode-hint">Indica almeno 2 partecipanti per avere posti avversario.</p>'}</div>
        </div>` : ''}
        <div class="auction-settings-actions">
          <button type="submit" class="auction-gate-btn auction-gate-btn--primary">Salva</button>
          <button type="button" class="auction-gate-btn" data-settings-action="new">Nuova asta</button>
          <button type="button" class="auction-gate-btn" data-settings-action="load">Carica altra</button>
          <button type="button" class="auction-gate-btn auction-gate-btn--danger" data-settings-action="delete">Elimina</button>
        </div>
        <p class="auction-gate-error" ${this.state.rosaSettingsError ? '' : 'hidden'}>
          ${escapeHtml(this.state.rosaSettingsError || '')}
        </p>
      </form>
    `;

    const form = body.querySelector('#auction-settings-form');

    form?.querySelectorAll('[data-exit-mode]')?.forEach((btn) => {
      btn.addEventListener('click', () => {
        this._captureAuctionSettingsDraftFromForm(form);
        const next = btn.dataset.exitMode === EXIT_MODE_ASSIGNED
          ? EXIT_MODE_ASSIGNED
          : EXIT_MODE_SIMPLE;
        this._auctionSettingsDraft.exitMode = next;
        if (next === EXIT_MODE_ASSIGNED) {
          this._auctionSettingsDraft.opponents = syncOpponentSlots(
            this._auctionSettingsDraft.opponentsStored?.length
              ? this._auctionSettingsDraft.opponentsStored
              : this._auctionSettingsDraft.opponents,
            this._auctionSettingsDraft.numParticipants,
          );
        }
        this._renderAuctionSettings();
      });
    });

    form?.querySelector('[name="numParticipants"]')?.addEventListener('input', (e) => {
      this._captureAuctionSettingsDraftFromForm(form);
      const raw = e.target.value;
      this._auctionSettingsDraft.numParticipants = raw === '' ? null : Number(raw) || null;
      if (this._auctionSettingsDraft.exitMode === EXIT_MODE_ASSIGNED) {
        this._auctionSettingsDraft.opponents = syncOpponentSlots(
          this._auctionSettingsDraft.opponents,
          this._auctionSettingsDraft.numParticipants,
        );
        this._auctionSettingsDraft.opponentsStored = this._auctionSettingsDraft.opponents.map((o) => ({ ...o }));
        this._renderAuctionSettings();
        form?.querySelector('[name="numParticipants"]')?.focus();
      }
    });

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const errorEl = form.querySelector('.auction-gate-error');
      try {
        const d = this._captureAuctionSettingsDraftFromForm(form);
        const exitMode = d.exitMode === EXIT_MODE_ASSIGNED
          ? EXIT_MODE_ASSIGNED
          : EXIT_MODE_SIMPLE;
        // Spegnere assegnate: non cancellare i nomi salvati
        const opponents = exitMode === EXIT_MODE_ASSIGNED
          ? syncOpponentSlots(d.opponents, d.numParticipants)
          : (Array.isArray(this.state.auctionOpponents) && this.state.auctionOpponents.length
            ? this.state.auctionOpponents
            : (d.opponentsStored || []));
        await this.saveActiveAuctionSettings({
          name: d.name,
          budget: d.budget,
          maxPlayers: d.maxPlayers,
          numParticipants: d.numParticipants,
          roleTargets: this.state.rosaRoleTargets || { ...DEFAULT_ROSA_ROLE_TARGETS },
          exitMode,
          opponents,
        });
        this._auctionSettingsDraft = null;
        this.closeAuctionSettings();
        this.renderRosaView?.();
        this.renderRoseAltriView?.();
        this.renderListaKpiStrip?.();
      } catch (err) {
        console.error(err);
        this.state.rosaSettingsError = 'Impostazioni non salvate. Riprova.';
        errorEl.textContent = this.state.rosaSettingsError;
        errorEl.hidden = false;
      }
    });

    body.querySelector('[data-settings-action="new"]')?.addEventListener('click', () => {
      this._auctionSettingsDraft = null;
      this.closeAuctionSettings();
      SessionPersistence.setActiveAuctionId(null);
      this.state.activeAuctionId = null;
      this.showUnifiedSetupScreen({ defaultMode: 'new' });
    });

    body.querySelector('[data-settings-action="load"]')?.addEventListener('click', () => {
      this._auctionSettingsDraft = null;
      this.closeAuctionSettings();
      SessionPersistence.setActiveAuctionId(null);
      this.state.activeAuctionId = null;
      this.showUnifiedSetupScreen({ defaultMode: 'load' });
    });

    body.querySelector('[data-settings-action="delete"]')?.addEventListener('click', async () => {
      if (!confirm('Eliminare questa asta (rosa e usciti inclusi)?')) return;
      try {
        this._auctionSettingsDraft = null;
        await this.deleteActiveAuction();
        this.closeAuctionSettings();
      } catch (err) {
        console.error(err);
        alert('Eliminazione fallita.');
      }
    });
  },
};
